import { useCallback } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import '@wsh/wsh.css'
import type { RouteContent } from '../constants/helpContent'
import { tourSeenKey } from '../constants/helpContent'

function buildIntroHtml(content: RouteContent, isDemoMode: boolean, isAuthenticated: boolean): string {
  const sectionsHtml = content.sections.map((s, i) => {
    const showMeBtn = s.element
      ? `<button class="wsh-show-me-btn" data-index="${i + 1}">Show me</button>`
      : ''

    return `
      <div class="wsh-intro-section${i > 0 ? ' wsh-intro-section--divider' : ''}">
        <div class="wsh-intro-section-icon">
          <i class="bi ${s.icon}"></i>
        </div>
        <div class="wsh-intro-section-body">
          <div class="wsh-intro-section-heading">
            <span>${s.heading}</span>
            ${showMeBtn}
          </div>
          <div class="wsh-intro-section-text">${s.body}</div>
        </div>
      </div>
    `
  }).join('')

  const hasHighlightSteps = content.sections.some(s => s.element)
  const tourCtaHtml = hasHighlightSteps
    ? `<div class="wsh-intro-tour-cta">
        <button class="wsh-intro-close-btn" type="button">Got it, close</button>
        <button class="wsh-full-tour-btn">
          <i class="bi bi-play-circle"></i> Take the full tour
        </button>
      </div>`
    : `<div class="wsh-intro-tour-cta">
        <button class="wsh-intro-close-btn" type="button">Got it, close</button>
      </div>`

  const bottomHtml = isDemoMode
    ? `<div class="wsh-intro-support">
        <a
          href="https://computeranything.dev/contact?projectType=wholesale-hub-demo"
          target="_blank"
          rel="noopener noreferrer"
          class="wsh-support-open-btn"
        >
          <i class="bi bi-arrow-up-right-circle"></i>
          Ready to use this for your business?
        </a>
      </div>`
    : isAuthenticated
      ? `<div class="wsh-intro-support">
          <button class="wsh-support-open-btn" type="button">
            <i class="bi bi-headset"></i>
            Still need help? Contact support
          </button>
        </div>`
      : ''

  return `
    <div class="wsh-intro-header">
      <img src="/wsh/wsh_logo_grn-landscape.png" alt="WholesaleHub" class="wsh-intro-logo" />
      <div class="wsh-intro-tagline">${content.title}, Help &amp; Guide</div>
      <div class="wsh-intro-subtitle">${content.intro}</div>
    </div>
    <div class="wsh-intro-body">
      ${sectionsHtml}
    </div>
    <div class="wsh-intro-footer">
      ${tourCtaHtml}
      ${bottomHtml}
    </div>
  `
}

function buildElementSteps(content: RouteContent) {
  const isMobile = window.innerWidth < 768
  return content.sections
    .filter(s => s.element)
    .map(s => {
      const selector = (isMobile && s.mobileElement) ? s.mobileElement : s.element!
      return {
        element: selector,
        popover: {
          title: s.heading,
          description: s.body,
          side: s.side as 'top' | 'bottom' | 'left' | 'right' | undefined,
          align: s.align as 'start' | 'center' | 'end' | undefined,
        },
      }
    })
}

export function useTour() {
  const runTour = useCallback((content: RouteContent, pathname: string, isDemoMode = false, isAuthenticated = false, onSupportClick?: () => void) => {
    const elementSteps = buildElementSteps(content)

    const steps = [
      {
        popover: {
          title: '',
          description: buildIntroHtml(content, isDemoMode, isAuthenticated),
          popoverClass: 'wsh-popover wsh-popover--intro',
          showButtons: ['close'] as ['close'],
          showProgress: false,
        },
      },
      ...elementSteps.map(step => ({
        element: step.element,
        popover: {
          title: step.popover.title,
          description: step.popover.description,
          popoverClass: 'wsh-popover',
          side: step.popover.side,
          align: step.popover.align,
        },
      })),
    ]

    document.body.style.overflow = 'hidden'

    const driverObj = driver({
      showProgress: true,
      animate: true,
      overlayOpacity: 0.55,
      smoothScroll: true,
      allowClose: true,
      overlayClickBehavior: () => {},
      disableActiveInteraction: true,
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Done',
      onCloseClick: () => driverObj.destroy(),
      onDestroyed: () => {
        document.body.style.overflow = ''
        localStorage.setItem(tourSeenKey(pathname), 'true')
      },
      onHighlightStarted: (element) => {
        if (!element) return
        const rect = element.getBoundingClientRect()
        if (rect.top < 80) {
          window.scrollBy({ top: rect.top - 90, behavior: 'smooth' })
        }
      },
      onPopoverRender: (popoverEl) => {
        popoverEl.wrapper.querySelectorAll<HTMLButtonElement>('.wsh-show-me-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            const index = parseInt(btn.dataset.index ?? '1', 10)
            driverObj.moveTo(index)
          })
        })

        const fullTourBtn = popoverEl.wrapper.querySelector<HTMLButtonElement>('.wsh-full-tour-btn')
        if (fullTourBtn) {
          fullTourBtn.addEventListener('click', () => driverObj.moveNext())
        }

        const introCloseBtn = popoverEl.wrapper.querySelector<HTMLButtonElement>('.wsh-intro-close-btn')
        if (introCloseBtn) {
          introCloseBtn.addEventListener('click', () => driverObj.destroy())
        }

        const supportBtn = popoverEl.wrapper.querySelector<HTMLButtonElement>('button.wsh-support-open-btn')
        if (supportBtn && onSupportClick) {
          supportBtn.addEventListener('click', () => {
            driverObj.destroy()
            onSupportClick()
          })
        }
      },
      steps,
    })

    driverObj.drive()
  }, [])

  const hasSeen = useCallback((pathname: string) => {
    return localStorage.getItem(tourSeenKey(pathname)) === 'true'
  }, [])

  const resetTour = useCallback((pathname: string) => {
    localStorage.removeItem(tourSeenKey(pathname))
  }, [])

  return { runTour, hasSeen, resetTour }
}
